import { useCallback, useEffect, useMemo, useState } from 'react';
import { ColumnOrderState, ColumnVisibilityState } from '@tanstack/react-table';
import { LegacyColumns } from '../types';

interface SavedColumnEntry {
  index: number;
}

type SavedColumns = Record<string, SavedColumnEntry>;

interface UseColumnPersistenceResult {
  columnVisibility: ColumnVisibilityState;
  setColumnVisibility: (visibility: ColumnVisibilityState) => void;
  columnOrder: ColumnOrderState;
  setColumnOrder: (order: ColumnOrderState) => void;
  isDefault: boolean;
  resetToDefaults: () => void;
}

// `truncate` columns render with flexible/expanding width (see TableCell.tsx); every other column
// has a fixed width. Hiding all truncate columns leaves the table with only fixed-width columns
// that don't fill the row, visibly breaking the layout. So truncate columns are always-visible in
// the column selector — can't be hidden.
export const getAlwaysVisibleKeys = (columns: LegacyColumns): Set<string> =>
  new Set(
    Object.entries(columns)
      .filter(([, c]) => c.truncate)
      .map(([key]) => key)
  );

const resolveDefault = (
  key: string,
  config: { default?: boolean | ((key: string) => boolean); hidden?: boolean },
  alwaysVisible: boolean
): boolean => {
  if (alwaysVisible) return true;
  if (config.hidden) return false;
  if (typeof config.default === 'function') return config.default(key);
  return config.default ?? true;
};

const buildDefaultVisibility = (columns: LegacyColumns): ColumnVisibilityState => {
  const alwaysVisibleKeys = getAlwaysVisibleKeys(columns);
  const visibility: ColumnVisibilityState = {};
  for (const [key, config] of Object.entries(columns)) {
    visibility[key] = resolveDefault(key, config, alwaysVisibleKeys.has(key));
  }
  return visibility;
};

const buildDefaultOrder = (columns: LegacyColumns): ColumnOrderState =>
  Object.entries(columns)
    .filter(([, c]) => !c.hidden)
    .sort(([, a], [, b]) => (a.index ?? 0) - (b.index ?? 0))
    .map(([key]) => key);

const storageKey = (handler: string) => `saved${handler}Columns`;

const loadFromStorage = (handler: string): SavedColumns | null => {
  try {
    const raw = window.localStorage.getItem(storageKey(handler));
    return raw ? (JSON.parse(raw) as SavedColumns) : null;
  } catch {
    return null;
  }
};

const saveToStorage = (handler: string, columns: SavedColumns, onColumnsChange?: (c: SavedColumns) => void) => {
  window.localStorage.setItem(storageKey(handler), JSON.stringify(columns));
  window.dispatchEvent(new CustomEvent('local_storage', { detail: { tableKey: handler } }));
  onColumnsChange?.(columns);
};

export const useColumnPersistence = (
  columns: LegacyColumns,
  tableKey: string | undefined,
  onColumnsChange?: (columns: SavedColumns) => void
): UseColumnPersistenceResult => {
  const defaultVisibility = useMemo(() => buildDefaultVisibility(columns), [columns]);
  const defaultOrder = useMemo(() => buildDefaultOrder(columns), [columns]);
  const alwaysVisibleKeys = useMemo(() => getAlwaysVisibleKeys(columns), [columns]);

  // Single source of truth: the raw persisted entry (null = use defaults)
  const [savedColumns, setSavedColumns] = useState<SavedColumns | null>(() =>
    tableKey ? loadFromStorage(tableKey) : null
  );

  // Derive visibility and order from savedColumns + columns (auto-updates when columns changes)
  const columnVisibility = useMemo<ColumnVisibilityState>(() => {
    if (!savedColumns) return defaultVisibility;
    const vis: ColumnVisibilityState = { ...defaultVisibility };
    for (const key of Object.keys(defaultVisibility)) {
      vis[key] = alwaysVisibleKeys.has(key) ? true : key in savedColumns;
    }
    return vis;
  }, [savedColumns, defaultVisibility, alwaysVisibleKeys]);

  const columnOrder = useMemo<ColumnOrderState>(() => {
    if (!savedColumns) return defaultOrder;
    return Object.entries(savedColumns)
      .sort(([, a], [, b]) => a.index - b.index)
      .map(([key]) => key)
      .filter((key) => key in columns);
  }, [savedColumns, defaultOrder, columns]);

  useEffect(() => {
    if (tableKey && savedColumns) {
      window.dispatchEvent(new CustomEvent('local_storage', { detail: { tableKey } }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isDefault = useMemo(
    () => JSON.stringify(columnVisibility) === JSON.stringify(defaultVisibility),
    [columnVisibility, defaultVisibility]
  );

  const persist = useCallback(
    (vis: ColumnVisibilityState, order: ColumnOrderState): SavedColumns => {
      const saved: SavedColumns = {};
      order.forEach((key, i) => {
        if (vis[key] !== false) saved[key] = { index: i + 1 };
      });
      if (tableKey) saveToStorage(tableKey, saved, onColumnsChange);
      return saved;
    },
    [tableKey, onColumnsChange]
  );

  const setColumnVisibility = useCallback(
    (vis: ColumnVisibilityState) => {
      // columnOrder only tracks currently visible columns. Re-added columns are missing from it,
      // so we must insert them back at their default positions before persisting.
      const trackedSet = new Set(columnOrder);
      const missing = defaultOrder.filter((key) => !trackedSet.has(key));

      if (missing.length === 0) {
        setSavedColumns(persist(vis, columnOrder));
        return;
      }

      const result = [...columnOrder];
      for (const key of missing) {
        const di = defaultOrder.indexOf(key);
        // Find the nearest predecessor in defaultOrder that is already in result
        let insertAt = 0;
        for (let i = di - 1; i >= 0; i--) {
          const pos = result.indexOf(defaultOrder[i]);
          if (pos !== -1) {
            insertAt = pos + 1;
            break;
          }
        }
        result.splice(insertAt, 0, key);
      }

      setSavedColumns(persist(vis, result));
    },
    [columnOrder, defaultOrder, persist]
  );

  const setColumnOrder = useCallback(
    (order: ColumnOrderState) => {
      const saved = persist(columnVisibility, order);
      setSavedColumns(saved);
    },
    [columnVisibility, persist]
  );

  const resetToDefaults = useCallback(() => {
    if (tableKey) {
      window.localStorage.removeItem(storageKey(tableKey));
      window.dispatchEvent(new CustomEvent('local_storage', { detail: { tableKey } }));
      const defaultSaved: SavedColumns = {};
      defaultOrder.forEach((key, i) => {
        if (defaultVisibility[key] !== false) defaultSaved[key] = { index: i + 1 };
      });
      onColumnsChange?.(defaultSaved);
    }
    setSavedColumns(null);
  }, [tableKey, defaultOrder, defaultVisibility, onColumnsChange]);

  return { columnVisibility, setColumnVisibility, columnOrder, setColumnOrder, isDefault, resetToDefaults };
};
